import { isDesktopApp } from "./desktop"

// This class is essential for adapting to the iOS onscreen keyboard.
//
// If the keyboard is present, then the visual viewport shrinks dramatically, and
// moves down the page, and if we want to avoid unpleasant things like the top bar hiding
// or the bottom of the edit window being below the keyboard off the screen, we have to:
//
//   1. shrink the height of the page (it's for this reason the entire page is
//      position: fixed. Does this on element with id "app"
//   2. if the visual viewport scrolls down, move the page to match where it is./
//      Does this by setting the "app" element top to match where the viewport is
//
//  Adds/removes the class "keyboard-visible" to #app.
class KeyboardDetector {
  maxVisualViewportHeight: number = 0
  canMobileKeyboardShow: "unknown" | "yes" | "no" = "unknown"
  appEl: HTMLDivElement
  bodyEl: HTMLBodyElement

  constructor() {
    this.appEl = document.getElementById("app") as HTMLDivElement
    if (!this.appEl) throw "Missing app element"

    this.bodyEl = document.getElementsByTagName("body")[0] as HTMLBodyElement
    if (!this.bodyEl) throw "Missing body element"

    if (window.visualViewport) {
      this.maxVisualViewportHeight = Math.max(
        window.visualViewport.height,
        this.maxVisualViewportHeight
      )
      window.visualViewport.addEventListener("scroll", (e) => {
        const height = (e?.target as any)?.height as number
        const offsetTop = (e?.target as any)?.offsetTop as number
        if (!height || !offsetTop) return
        this.onVisualViewportScroll(height, offsetTop)
      })
      window.visualViewport.addEventListener("resize", (e) => {
        const height = (e?.target as any)?.height
        const offsetTop = (e?.target as any)?.offsetTop as number
        if (!height) return
        this.onVisualViewportResize(height, offsetTop)
      })
      this.onVisualViewportResize(
        window.visualViewport.height,
        window.visualViewport.offsetTop
      )
    } else {
      console.log(
        "no visual viewport API, mobile behaviour will degrade slightly"
      )
    }
    this.checkForOpenKeyboardAndEditorFocus()
  }

  onVisualViewportScroll(newHeight: number, newTop: number) {
    if (newHeight > this.maxVisualViewportHeight)
      this.maxVisualViewportHeight = newHeight

    this.appEl.style.height = newHeight + "px"
    if (newTop >= 0 && newHeight < this.maxVisualViewportHeight * 0.66) {
      this.appEl.style.top = newTop + "px"
    } else {
      this.appEl.style.top = "0px"
    }
  }

  onVisualViewportResize(newHeight: number, newTop: number) {
    if (newHeight > this.maxVisualViewportHeight)
      this.maxVisualViewportHeight = newHeight

    this.appEl.style.height = newHeight + "px"
    // this.bodyEl.style.height = newHeight + "px"
    this.checkForOpenKeyboardAndEditorFocus()
    if (newTop >= 0 && newHeight < this.maxVisualViewportHeight * 0.66) {
      this.appEl.style.top = newTop + "px"
    } else {
      this.appEl.style.top = "0px"
    }
  }

  isMobileKeyboardOpen() {
    // A desktop window made shorter isn't a keyboard opening, and hiding the
    // home bar there would leave nothing to drag the window by.
    if (isDesktopApp()) return false
    return (
      window.visualViewport &&
      window.visualViewport.height < this.maxVisualViewportHeight * 0.66
    )
  }

  isEditorFocussed(): boolean {
    const el = document.activeElement as HTMLElement
    return el.contentEditable == "true"
  }

  checkForOpenKeyboardAndEditorFocus() {
    if (this.isMobileKeyboardOpen()) {
      this.canMobileKeyboardShow = "yes"
    } else if (this.isEditorFocussed() && window.visualViewport) {
      if (this.canMobileKeyboardShow === "unknown") {
        this.canMobileKeyboardShow = "no"
      }
    }
    if (this.canMobileKeyboardShow === "no") {
      this.appEl.classList.remove("compressed-header")
    } else {
      if (window.innerWidth < 500) {
        this.appEl.classList.add("compressed-header")
      }
    }
    if (this.isEditorFocussed() && this.canMobileKeyboardShow == "yes") {
      this.appEl.classList.add("keyboard-visible")
    } else {
      this.appEl.classList.remove("keyboard-visible")
    }

    // Specific case: if we're on a platform where we know the mobile keyboard can
    // show, and we can detect it, and it is not present, then flip back.
    if (
      window.visualViewport &&
      this.canMobileKeyboardShow == "yes" &&
      !this.isMobileKeyboardOpen()
    ) {
      this.appEl.classList.remove("keyboard-visible")
    }
  }
}

export default KeyboardDetector
